import { z } from "zod";

/**
 * Protocol version of the agent <-> server WebSocket connection.
 * Version 1 is the baseline for racler.dev v1.0.0.
 */
export const PROTOCOL_VERSION = 1;

/**
 * Well-known capabilities negotiated between desktop/agent and server.
 * Allows additive feature evolution without requiring exact semantic version locks.
 */
export const KNOWN_CAPABILITIES = [
  "changesets-v1",
  "durable-runs-v1",
  "worktrees-v1",
  "lsp-v1",
] as const;

export type Capability = (typeof KNOWN_CAPABILITIES)[number] | (string & {});

/** Default capabilities supported by the standard v1 agent runtime. */
export const DEFAULT_CLIENT_CAPABILITIES: readonly string[] = [
  "changesets-v1",
  "durable-runs-v1",
  "worktrees-v1",
  "lsp-v1",
];

/** Structured identity and capability reporting of the connected client. */
export const clientInfoSchema = z.looseObject({
  /** "racler-desktop" for native macOS app, "racler-agent" for headless CLI/LaunchAgent. */
  product: z.string().max(64),
  /** Authoritative semver of the client application (e.g. "0.1.0" or "1.0.0"). */
  version: z.string().max(64),
  /** Host platform (e.g. "darwin"). */
  platform: z.string().max(64),
  /** CPU architecture (e.g. "arm64", "x64"). */
  arch: z.string().max(64),
  /** Wire protocol version spoken by the client. */
  protocolVersion: z.number().int().positive().default(PROTOCOL_VERSION),
  /** List of feature capabilities supported by this client. */
  capabilities: z.array(z.string().max(64)).default(() => [...DEFAULT_CLIENT_CAPABILITIES]),
});
export type ClientInfo = z.infer<typeof clientInfoSchema>;

/** Structured server version and capability response returned in auth.ok. */
export const serverInfoSchema = z.looseObject({
  protocolVersion: z.number().int().positive().default(PROTOCOL_VERSION),
  capabilities: z.array(z.string().max(64)).default(() => [...DEFAULT_CLIENT_CAPABILITIES]),
  /** Optional minimum desktop version required by this server. */
  minimumSupportedDesktopVersion: z.string().max(64).optional(),
});
export type ServerInfo = z.infer<typeof serverInfoSchema>;

/** Human- and machine-readable message returned when a client must be updated. */
export const UPGRADE_REQUIRED_MESSAGE = "Your racler.dev Desktop app must be updated.";

/**
 * Pure semver comparator for version gating without external runtime dependencies.
 * Returns negative if a < b, 0 if a == b, positive if a > b.
 * Ignores leading 'v'.
 */
export function compareSemver(a: string, b: string): number {
  const parse = (v: string): { major: number; minor: number; patch: number; prerelease: string | null } => {
    const clean = v.trim().replace(/^v/i, "");
    const [core, ...pre] = clean.split("-");
    const parts = (core ?? "").split(".").map((n) => {
      const parsed = parseInt(n, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    });
    return {
      major: parts[0] ?? 0,
      minor: parts[1] ?? 0,
      patch: parts[2] ?? 0,
      prerelease: pre.length > 0 ? pre.join("-") : null,
    };
  };

  const pa = parse(a);
  const pb = parse(b);

  if (pa.major !== pb.major) return pa.major - pb.major;
  if (pa.minor !== pb.minor) return pa.minor - pb.minor;
  if (pa.patch !== pb.patch) return pa.patch - pb.patch;

  // Standard semver rule: a version with prerelease has lower precedence than a version without.
  if (pa.prerelease && !pb.prerelease) return -1;
  if (!pa.prerelease && pb.prerelease) return 1;
  if (pa.prerelease && pb.prerelease) {
    return pa.prerelease.localeCompare(pb.prerelease);
  }

  return 0;
}

/**
 * Evaluates whether a client version satisfies the minimum supported requirement.
 * If no minimum is set, any version is compatible.
 */
export function isClientCompatible(
  clientVersion: string | undefined | null,
  minimumSupportedVersion: string | undefined | null,
): boolean {
  if (!minimumSupportedVersion) return true;
  if (!clientVersion) return false;
  return compareSemver(clientVersion, minimumSupportedVersion) >= 0;
}
